import { ShieldCheck, FileText } from "lucide-react";

/**
 * Verified Analytics badge — the premium aspirational trust marker.
 *
 * Only renders the green/volt verified treatment when ALL of:
 *   - The athlete has at least one OAuth-verified social connection
 *   - That connection's account_type is "creator" or "business"
 *   - The athlete is on a paid plan (per the spec — verified is a Pro benefit)
 *
 * Personal-account athletes (or anyone on Free) see the muted "Manual Stats"
 * label so the difference is felt without being judgemental.
 *
 * Usage:
 *   <VerifiedAnalyticsBadge connections={socials} plan={user.plan} />
 *   <VerifiedAnalyticsBadge variant="compact" ... />
 *   <VerifiedAnalyticsBadge variant="public" ... />   // for share/kit pages
 */
const PAID_PLANS = new Set(["pro", "brand_pro", "agency", "brand_lite"]);
const VERIFIED_TYPES = new Set(["creator", "business"]);


export function getVerifiedStatus({ connections, plan }) {
  if (!connections || !Array.isArray(connections) || connections.length === 0) {
    return { tier: "none", verifiedProviders: [], hasManual: false };
  }
  const verifiedProviders = connections
    .filter(c => c.source === "oauth" && c.status === "connected" &&
                  VERIFIED_TYPES.has((c.metrics?.account_type || "").toLowerCase()))
    .map(c => c.provider);
  const hasManual = connections.some(c => c.source === "manual");
  const paid = PAID_PLANS.has((plan || "").toLowerCase());
  if (verifiedProviders.length > 0 && paid) return { tier: "verified", verifiedProviders, hasManual };
  if (verifiedProviders.length > 0) return { tier: "verified_unpaid", verifiedProviders, hasManual };
  if (hasManual) return { tier: "manual", verifiedProviders: [], hasManual: true };
  return { tier: "none", verifiedProviders: [], hasManual: false };
}


export default function VerifiedAnalyticsBadge({ connections, plan, variant = "default", hideManual = false }) {
  const { tier, verifiedProviders } = getVerifiedStatus({ connections, plan });

  if (tier === "none") return null;
  if (tier === "manual" && hideManual) return null;

  if (tier === "verified") {
    return (
      <span data-testid="badge-verified-analytics"
        className={`inline-flex items-center gap-1.5 border border-volt text-volt uppercase tracking-widest font-bold ${
          variant === "compact" ? "px-2 py-0.5 text-[9px]"
          : variant === "public" ? "px-3 py-1.5 text-[11px] bg-volt/10"
          : "px-2.5 py-1 text-[10px]"
        }`}
        title={`Verified via ${verifiedProviders.join(" + ")}`}>
        <ShieldCheck className={variant === "public" ? "w-3.5 h-3.5" : "w-3 h-3"} />
        Verified analytics
      </span>
    );
  }

  if (tier === "verified_unpaid") {
    return (
      <span data-testid="badge-verified-unpaid"
        className={`inline-flex items-center gap-1.5 border border-line text-ink-muted uppercase tracking-widest ${
          variant === "compact" ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]"
        }`}
        title="Account verified — upgrade to Pro to display the badge publicly">
        <ShieldCheck className="w-3 h-3 opacity-60" /> Verified · Pro
      </span>
    );
  }

  // manual
  return (
    <span data-testid="badge-manual-stats"
      className={`inline-flex items-center gap-1.5 border border-line text-ink-muted uppercase tracking-widest ${
        variant === "compact" ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]"
      }`}>
      <FileText className="w-3 h-3" /> Manual stats
    </span>
  );
}
